/* eslint-disable */
// v2
import { SUPABASE_URL, SUPABASE_ANON_KEY, RAZORPAY_KEY_ID } from './security/supabaseClient';
import { PAYMENTS_ENABLED, PAYMENTS_OFF_MESSAGE } from './config/payments';

// ─── SUPPORTED CURRENCIES ──────────────────────────────────────────────────────
// These rates are not cosmetic. toSmallestUnit() runs the USD price through
// them to produce the amount Razorpay actually charges, so a stale rate
// undercharges or overcharges a real customer on every transaction.
//
// Last refreshed 2026-10-02 from exchangerate-api.com. The previous set was
// from March 2026 and INR had drifted 16% — a $100 booking was charging ₹8,300
// instead of ₹9,638 on the largest corridor we have.
//
// IDR, THB and NPR are here because SchoolDashboard offers them to schools.
// They were missing, and the `|| CURRENCIES.USD` fallback below silently
// treated an IDR price as dollars.
//
// Hardcoding is the wrong long-term answer for something that moves daily —
// see RATES_AS_OF and the warning under it.
export const RATES_AS_OF = "2026-10-02";

export const CURRENCIES = {
  USD: { code:"USD", symbol:"$",   name:"US Dollar",          flag:"🇺🇸", rate:1,        subunit:100 },
  INR: { code:"INR", symbol:"₹",   name:"Indian Rupee",       flag:"🇮🇳", rate:96.38,    subunit:100 },
  EUR: { code:"EUR", symbol:"€",   name:"Euro",               flag:"🇪🇺", rate:0.887,    subunit:100 },
  GBP: { code:"GBP", symbol:"£",   name:"British Pound",      flag:"🇬🇧", rate:0.757,    subunit:100 },
  SGD: { code:"SGD", symbol:"S$",  name:"Singapore Dollar",   flag:"🇸🇬", rate:1.280,    subunit:100 },
  AED: { code:"AED", symbol:"د.إ", name:"UAE Dirham",         flag:"🇦🇪", rate:3.6725,   subunit:100 },
  AUD: { code:"AUD", symbol:"A$",  name:"Australian Dollar",  flag:"🇦🇺", rate:1.443,    subunit:100 },
  CAD: { code:"CAD", symbol:"C$",  name:"Canadian Dollar",    flag:"🇨🇦", rate:1.423,    subunit:100 },
  JPY: { code:"JPY", symbol:"¥",   name:"Japanese Yen",       flag:"🇯🇵", rate:157.92,   subunit:1   },
  IDR: { code:"IDR", symbol:"Rp",  name:"Indonesian Rupiah",  flag:"🇮🇩", rate:17970.43, subunit:100 },
  THB: { code:"THB", symbol:"฿",   name:"Thai Baht",          flag:"🇹🇭", rate:33.67,    subunit:100 },
  NPR: { code:"NPR", symbol:"₨",   name:"Nepali Rupee",       flag:"🇳🇵", rate:154.15,   subunit:100 },
};

// One list, so the picker a school sees can never again offer a currency
// checkout cannot convert. SchoolDashboard imports this instead of keeping
// its own copy.
export const CURRENCIES_LIST = Object.values(CURRENCIES);

// Rates drift. This one went six months without being touched and nothing
// said so. Noisy on purpose, and only in development.
export function ratesAgeDays() {
  return Math.floor((Date.now() - new Date(RATES_AS_OF + "T00:00:00Z").getTime()) / 86400000);
}
if (process.env.NODE_ENV !== "production" && ratesAgeDays() > 30) {
  console.warn(
    `[PaymentService] Exchange rates are ${ratesAgeDays()} days old (set ${RATES_AS_OF}). ` +
    `They decide what customers are charged — refresh CURRENCIES or move to a live feed.`
  );
}

// Convert USD amount to the target currency
export const convertFromUsd = (usdAmount, currencyCode) => {
  const cur = CURRENCIES[currencyCode] || CURRENCIES.USD;
  return Math.round(usdAmount * cur.rate * 100) / 100;
};

// Convert an amount in `currencyCode` to USD. Unknown currencies give NaN, so
// callers comparing prices skip them rather than treating them as dollars.
export const toUsd = (amount, currencyCode = "USD") => {
  const cur = CURRENCIES[currencyCode];
  return cur ? Number(amount) / cur.rate : NaN;
};

// Format a USD amount as a string in the target currency
export const formatAmount = (usdAmount, currencyCode) => {
  const cur = CURRENCIES[currencyCode] || CURRENCIES.USD;
  const converted = convertFromUsd(usdAmount, currencyCode);
  const decimals = cur.subunit === 1 ? 0 : 0; // show whole numbers for cleanliness
  return `${cur.symbol}${converted.toLocaleString(undefined, { maximumFractionDigits: decimals })}`;
};

// Amount in smallest unit for Razorpay (paise, cents, etc.)
export const toSmallestUnit = (usdAmount, currencyCode) => {
  const cur = CURRENCIES[currencyCode] || CURRENCIES.USD;
  const converted = convertFromUsd(usdAmount, currencyCode);
  return Math.round(converted * cur.subunit);
};

// Legacy helpers (kept for backward compatibility)
export const usdToInr = (usd) => Math.round(usd * 83);
export const usdToInrPaise = (usd) => Math.round(usd * 83 * 100);
export const formatInr = (usd) => `₹${usdToInr(usd).toLocaleString("en-IN")}`;

// Load Razorpay script
export const loadRazorpay = () => new Promise((resolve) => {
  if (!PAYMENTS_ENABLED) { resolve(false); return; }
  if (window.Razorpay) { resolve(true); return; }
  const script = document.createElement("script");
  script.src = "https://checkout.razorpay.com/v1/checkout.js";
  script.onload = () => resolve(true);
  script.onerror = () => resolve(false);
  document.body.appendChild(script);
});

// ─── MAIN PAYMENT FUNCTION ─────────────────────────────────────────────────────
// NOTE: payment records and booking payment_status are written exclusively by the
// razorpay-webhook Edge Function (service role) after verifying the signature.
// The client makes no direct writes to `payments` or bookings — this is the
// fraud-prevention boundary. Routes payouts are handled by the webhook on
// transfer.settled / transfer.failed events.
export const initiatePayment = async ({
  amount,                    // USD amount (base price stored in DB)
  currency = "INR",          // selected currency code, e.g. "USD", "EUR"
  bookingId,
  bookingType,               // 'class' | 'addon' | 'online' | 'retreat' | 'accommodation'
  commissionRate = 15,       // actual tier-aware rate from BookingFlow
  studentName,
  studentEmail,
  studentPhone,
  schoolName,
  description,
  schoolId,
  studentId,
  userToken,                 // JWT from auth — required to call create-razorpay-order
  schoolLinkedAccountId,     // razorpay_linked_account_id from school record
  isDeposit = false,         // true when paying deposit portion of accommodation
  wasDepositPaid = false,    // true when paying the outstanding balance
  onSuccess,
  onFailure,
  onDismiss,
}) => {

  // Payments off: refuse before loading the script or asking for an order.
  // Callers render the request-to-book flow instead, so reaching this is a bug.
  if (!PAYMENTS_ENABLED) {
    console.warn("[PaymentService] initiatePayment called while PAYMENTS_ENABLED is off");
    onFailure?.(PAYMENTS_OFF_MESSAGE);
    return;
  }

  const loaded = await loadRazorpay();
  if (!loaded) {
    onFailure?.("Could not load payment gateway. Please check your internet connection.");
    return;
  }

  const key = RAZORPAY_KEY_ID;
  if (!key) {
    onFailure?.("Razorpay key not configured.");
    return;
  }

  const cur            = CURRENCIES[currency] || CURRENCIES.INR;
  const amountSmallest = toSmallestUnit(amount, currency);
  const amountDisplay  = convertFromUsd(amount, currency);

  // ── Step 1: Create Razorpay order server-side ────────────────────────────
  // This is the key fraud-prevention step. The order is created with our secret
  // key on the server, so its amount cannot be tampered with by the client.
  // The webhook will only accept payment.captured events that match a known order_id.
  let serverOrderId = null;
  try {
    const orderRes = await fetch(`${SUPABASE_URL}/functions/v1/create-razorpay-order`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${userToken || SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        amount_smallest: amountSmallest,
        currency:        cur.code,
        booking_id:      bookingId,
        booking_type:    bookingType,
        is_deposit:      isDeposit,
        was_deposit_paid: wasDepositPaid,
        // Carried into the order/payment notes so the webhook can write the
        // payments audit log server-side (client no longer writes payments).
        school_id:       schoolId || null,
        commission_rate: commissionRate || 15,
        amount_usd:      amount,
      }),
    });

    if (orderRes.ok) {
      const orderData = await orderRes.json();
      serverOrderId = orderData.order_id;
    } else {
      const errBody = await orderRes.json().catch(() => ({}));
      console.error("[PaymentService] create-razorpay-order failed:", errBody);
      // Non-blocking: if order creation fails we still open the modal without order_id.
      // The webhook will still verify the payment signature.
    }
  } catch (e) {
    console.warn("[PaymentService] create-razorpay-order network error:", e);
  }

  // ── Step 2: Open Razorpay modal ───────────────────────────────────────────
  const options = {
    key,
    amount:   amountSmallest,
    currency: cur.code,
    order_id: serverOrderId || undefined,  // ties the modal to the server-created order
    name:     "YogaStay",
    description: description || `Booking at ${schoolName}`,
    image: "",
    prefill: {
      name:    studentName || "",
      email:   studentEmail || "",
      contact: studentPhone || "",
    },
    notes: {
      booking_id:       bookingId,
      booking_type:     bookingType,
      school_name:      schoolName,
      amount_usd:       `$${amount}`,
      currency_selected: cur.code,
      is_deposit:       String(isDeposit),
      was_deposit_paid: String(wasDepositPaid),
    },
    theme: { color: "#B5703F" },
    modal: { ondismiss: () => { onDismiss?.(); } },
    handler: async (response) => {
      // ── No client-side writes ────────────────────────────────────────────
      // The razorpay-webhook Edge Function (service role) is the SOLE writer of
      // the payments audit log and of payment_status / razorpay ids on the
      // booking, after verifying the HMAC-SHA256 signature. The browser writes
      // nothing here — this is what prevents client-side payment fraud and lets
      // the `payments` table's open RLS policies be removed.

      // Immediately notify the UI that the user completed payment in the modal.
      // The booking won't be "confirmed" until the webhook fires (~1-3 seconds),
      // but we show a success screen optimistically. If the webhook fails to fire,
      // the school dashboard will show the booking as pending until reconciled.
      onSuccess?.({
        paymentId:      response.razorpay_payment_id,
        amountDisplay,
        currencySymbol: cur.symbol,
        currencyCode:   cur.code,
        amountUsd:      amount,
        amountInr:      currency === "INR" ? amountDisplay : usdToInr(amount),
      });
    },
  };

  const rzp = new window.Razorpay(options);

  rzp.on("payment.failed", (response) => {
    // The webhook records payment.failed server-side; the client only updates UI.
    onFailure?.(response.error?.description || "Payment failed. Please try again.");
  });

  rzp.open();
};
